<script setup>
import { useLayout } from '@/layout/common/composables/layout';
import { useDirection } from '@/composables/useDirection';
import { useLanguageStore } from '@/stores/language';
import DashboardBreadcrumb from './DashboardBreadcrumb.vue';
import DashboardHeader from './DashboardHeader.vue';
import DashboardSidebar from './DashboardSidebar.vue';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

// Saudi Tech horizontal logos
import saudiTechHorizontalEn from '@/assets/images/saudi-tech/saudi-tech-horizontal-en.svg';
import saudiTechHorizontalAr from '@/assets/images/saudi-tech/saudi-tech-horizontal-ar.svg';

const { calculatedMenuMode, layoutConfig } = useLayout();
const { isRtl } = useDirection();
const { t } = useLanguageStore();
const route = useRoute();
const isHomeDashboard = computed(() => route.name === 'home-default');

// The shell chrome (header position, sidebar-integrated layout, page padding)
// differs quite a bit between the Home dashboard and every other page. If we
// flip that chrome the instant `route.name` changes, it snaps into its new
// layout while the outgoing page is still mid-way through its leave
// transition — producing a visible jump instead of a smooth crossfade.
// Keep the previous chrome until its page finishes leaving. Routed pages share
// the same grid cell, so they can crossfade without creating a blank frame or
// briefly expanding the layout while both are mounted.
const shellIsHome = ref(isHomeDashboard.value);
function syncShellAfterLeave() {
    shellIsHome.value = isHomeDashboard.value;
}
// Safety net: if a route change happens without a leave transition firing
// (e.g. reduced-motion, or the very first navigation), keep the chrome in
// sync anyway.
watch(isHomeDashboard, (value) => {
    if (shellIsHome.value !== value) {
        requestAnimationFrame(() => {
            shellIsHome.value = isHomeDashboard.value;
        });
    }
});
</script>

<template>
    <div
        :class="[
            'sfh-app-shell h-dvh relative flex overflow-hidden',
            calculatedMenuMode === 'horizontal' ? 'sfh-app-shell--horizontal flex-col' : 'flex-row'
        ]"
        :data-sidebar-state="layoutConfig.menuState"
        :data-menu-mode="calculatedMenuMode"
    >
        <a href="#main-content" class="skip-link">
            {{ t('Skip to main content') }}
        </a>
        <DashboardSidebar />
        <main
            :class="[
                'sfh-main-shell min-h-0 min-w-0 flex-1 flex flex-col transition-all duration-300 overflow-hidden',
                {
                    'sfh-main-shell--home': shellIsHome,
                    'sfh-main-shell--horizontal w-full': calculatedMenuMode === 'horizontal'
                }
            ]"
        >
            <!-- <DashboardBreadcrumb /> -->
            <div
                id="main-content"
                tabindex="-1"
                class="flex-1 overflow-auto"
                :class="{ 'home-scroll-content': shellIsHome }"
            >
                <!-- Keep one header instance mounted across route changes. Moving the
                     component between two v-if branches can invalidate the DOM anchor
                     used by its PrimeVue overlays while Vue is flushing navigation. -->
                <DashboardHeader
                    :class="shellIsHome ? 'home-integrated-header' : 'main-shell-header'"
                />
                <div
                    :class="[
                        'sfh-page-shell',
                        { 'sfh-page-shell--bare': shellIsHome }
                    ]"
                >
                    <router-view v-slot="{ Component, route: activeRoute }">
                        <Transition name="page" @after-leave="syncShellAfterLeave">
                            <component :is="Component" :key="activeRoute.path" />
                        </Transition>
                    </router-view>
                </div>
            </div>

            <!-- Footer -->
            <footer class="footer-container">
                <div class="footer-content">
                    <p class="footer-text">
                        {{ t('Designed & Developed by') }}
                        <a
                            href="https://empower-sa.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            :title="t('Empower Co. (opens in a new window)')"
                            class="footer-link"
                        >
                            <span class="footer-company">@Empower Co</span>
                            <span class="sr-only">{{ t('(opens in a new window)') }}</span>
                            <i class="pi pi-external-link ml-1 text-[0.65rem]" aria-hidden="true"></i>
                        </a>
                    </p>

                    <span class="footer-divider" aria-hidden="true"></span>

                    <!-- Saudi Tech Logo -->
                    <div class="footer-saudi-tech">
                        <img
                            :src="isRtl ? saudiTechHorizontalAr : saudiTechHorizontalEn"
                            :alt="t('Saudi Tech')"
                            class="saudi-tech-logo"
                            width="48"
                            height="20"
                        />
                    </div>
                </div>
            </footer>
        </main>
        <Toast />
    </div>
</template>

<style scoped>
/* Footer Styles */
.footer-container {
    background: var(--surface-card);
    border-top: 1px solid var(--border);
    padding: 0.45rem 1.25rem;
    margin-top: auto;
    box-shadow: 0 -4px 16px rgba(14, 73, 120, 0.04);
    backdrop-filter: blur(10px);
}

.footer-content {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 0.5rem 1rem;
    flex-wrap: wrap;
    /* max-width: 1200px; */
    margin: 0 auto;
}

/* Saudi Tech Logo */
.footer-saudi-tech {
    display: flex;
    align-items: center;
    justify-content: center;
    /* padding: 0.15rem 0.5rem; */
    /* border-radius: 999px; */
    background: rgba(14, 73, 120, 0.04);
    /* border: 1px solid rgba(14, 73, 120, 0.08); */
}

.saudi-tech-logo {
    height: 1.2rem;
    width: auto;
    object-fit: contain;
    transition: opacity 0.3s ease, filter 0.3s ease, transform 0.3s ease;
}

.saudi-tech-logo:hover {
    opacity: 0.85;
    transform: translateY(-1px);
}

.footer-text {
    font-size: 0.8rem;
    color: var(--text-muted-color);
    margin: 0;
    text-align: center;
    line-height: 1.4;
}

.footer-company {
    font-weight: 600;
    color: var(--p-primary-800, #1a433f);
}

.footer-link {
    text-decoration: none;
}

.footer-divider {
    width: 4px;
    height: 4px;
    border-radius: 999px;
    background: rgba(14, 73, 120, 0.25);
}

.skip-link {
    position: fixed;
    inset-block-start: 0.5rem;
    inset-inline-start: 0.5rem;
    z-index: 2000;
    padding: 0.65rem 1rem;
    border-radius: 0.5rem;
    background: var(--p-surface-0);
    color: var(--p-primary-800);
    font-weight: 700;
    transform: translateY(-150%);
}

.skip-link:focus {
    transform: translateY(0);
}

.sfh-main-shell--horizontal {
    inline-size: 100%;
    align-self: center;
}

.sfh-main-shell :deep(.main-shell-header) {
    position: sticky;
    inset-block-start: 0;
    z-index: 50;
    flex-shrink: 0;
}

@media (min-width: 768px) {
    .sfh-main-shell--home .home-scroll-content {
        position: relative;
        container-name: home-shell;
        container-type: inline-size;
    }

    .sfh-main-shell--home :deep(.home-integrated-header) {
        position: absolute;
        z-index: 40;
        inset-block-start: 1.5rem;
        inset-inline: auto;
        left: 50%;
        width: min(calc(100% - 3rem), 100rem);
        max-width: 100rem;
        min-width: 0;
        min-height: 4.5rem;
        max-height: none !important;
        padding: .65rem .75rem !important;
        box-sizing: border-box;
        border: 0 !important;
        background: transparent !important;
        box-shadow: none !important;
        transform: translateX(-50%);
        backdrop-filter: none !important;
    }

    .sfh-main-shell--home :deep(.home-integrated-header.dashboard-header) {
        display: grid !important;
        grid-template-columns: minmax(10rem, 1fr) minmax(20rem, 34rem) minmax(10rem, 1fr);
        column-gap: clamp(.75rem, 1.4vw, 1.5rem);
    }

    .sfh-main-shell--home :deep(.dashboard-header__search) {
        min-width: 0;
        width: 100% !important;
        justify-self: center;
    }

    .sfh-main-shell--home :deep(.dashboard-header__leading) {
        min-width: 0;
    }

    .sfh-main-shell--home :deep(.dashboard-header__actions) {
        min-width: max-content;
        justify-self: end;
        gap: .65rem !important;
    }

    .sfh-main-shell--home :deep(.header-icon-control) {
        width: 2.9rem !important;
        height: 2.9rem !important;
        border-color: rgba(255, 255, 255, .72) !important;
        border-radius: 999px !important;
        color: #263332 !important;
        background: rgba(255, 255, 255, .9) !important;
        box-shadow: 0 8px 22px rgba(14, 77, 79, .12) !important;
        backdrop-filter: blur(14px);
    }

    .sfh-main-shell--home :deep(.header-language-control) {
        width: 2.9rem !important;
        height: 2.9rem !important;
        border-color: rgba(255, 255, 255, .72) !important;
        border-radius: 999px !important;
        background: rgba(255, 255, 255, .9) !important;
        box-shadow: 0 8px 22px rgba(14, 77, 79, .12) !important;
        backdrop-filter: blur(14px);
    }

    .sfh-main-shell--home :deep(.spotlight-search .p-inputtext) {
        height: 3.25rem;
        border-radius: 999px !important;
        border-color: rgba(255, 255, 255, .72) !important;
        background: rgba(255, 255, 255, .92) !important;
        box-shadow: 0 8px 24px rgba(14, 77, 79, .12) !important;
        backdrop-filter: blur(16px);
    }

    .sfh-main-shell--home :deep(.header-profile-control img) {
        width: 2.9rem !important;
        height: 2.9rem !important;
        border-radius: 999px !important;
        box-shadow: 0 0 0 3px rgba(255, 255, 255, .78), 0 8px 20px rgba(14, 77, 79, .15) !important;
    }

    .sfh-main-shell--home :deep(.home-breadcrumb-brand) {
        color: #fff;
        text-shadow: 0 2px 10px rgba(10, 64, 66, .18);
    }

    .sfh-main-shell--home :deep(.spotlight-shortcut) {
        border-color: rgba(0, 125, 137, .1);
        background: rgba(255, 255, 255, .84);
    }
}

/* The available content width changes when the static sidebar collapses, even
   though the viewport does not. Container queries keep the integrated header
   responsive to that real width. */
@container home-shell (max-width: 70rem) {
    .sfh-main-shell--home :deep(.home-integrated-header.dashboard-header) {
        grid-template-columns: auto minmax(14rem, 1fr) auto;
        column-gap: .65rem;
    }

    .sfh-main-shell--home :deep(.dashboard-header__leading .home-breadcrumb-brand span:last-child) {
        display: none;
    }

    .sfh-main-shell--home :deep(.dashboard-header__actions) {
        gap: .4rem !important;
    }

    .sfh-main-shell--home :deep(.header-icon-control),
    .sfh-main-shell--home :deep(.header-language-control),
    .sfh-main-shell--home :deep(.header-profile-control img) {
        width: 2.65rem !important;
        height: 2.65rem !important;
    }
}

@media (min-width: 768px) and (max-width: 1120px) {
    .sfh-main-shell--home :deep(.home-integrated-header.dashboard-header) {
        grid-template-columns: auto minmax(16rem, 1fr) auto;
    }

    .sfh-main-shell--home :deep(.dashboard-header__leading .home-breadcrumb-brand span:last-child) {
        display: none;
    }

    .sfh-main-shell--home :deep(.dashboard-header__actions) {
        gap: .4rem !important;
    }

    .sfh-main-shell--home :deep(.header-icon-control),
    .sfh-main-shell--home :deep(.header-language-control),
    .sfh-main-shell--home :deep(.header-profile-control img) {
        width: 2.65rem !important;
        height: 2.65rem !important;
    }
}

@media (forced-colors: active) {
    .footer-company {
        background: none;
        color: LinkText;
        -webkit-text-fill-color: currentColor;
    }
}

/* Responsive */
@media (max-width: 640px) {
    .footer-container {
        padding: 0.4rem 0.75rem;
    }

    .saudi-tech-logo {
        height: 1rem;
    }

    .footer-text {
        font-size: 0.7rem;
    }

    .footer-content {
        flex-direction: column;
        gap: 0.5rem;
    }

    .footer-divider {
        display: none;
    }
}
</style>

<!-- Dark Mode (must be unscoped) -->
<style>
.app-dark .footer-container {
    background: var(--surface-ground);
    border-top-color: var(--border);
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.2);
}

.app-dark .footer-text {
    color: var(--text-muted-color);
}

.app-dark .footer-company {
    color: var(--p-primary-300, #7ac9be);
}

/* Invert the green Saudi Tech logo to white for dark mode */
.app-dark .saudi-tech-logo {
    filter: brightness(0) invert(1);
}

.app-dark .footer-saudi-tech {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
}

.app-dark .footer-divider {
    background: rgba(255, 255, 255, 0.35);
}

/* Home header sits over the overview artwork, so it needs its own dark glass treatment. */
.app-dark .sfh-main-shell--home .home-integrated-header .header-icon-control,
.app-dark .sfh-main-shell--home .home-integrated-header .header-language-control {
    color: #9de1e3 !important;
    border-color: rgba(139, 219, 222, 0.2) !important;
    background: rgba(9, 31, 33, 0.82) !important;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.06) !important;
}

.app-dark .sfh-main-shell--home .home-integrated-header .header-icon-control > i,
.app-dark .sfh-main-shell--home .home-integrated-header .header-language-control i {
    color: inherit !important;
    opacity: 1;
}

.app-dark .sfh-main-shell--home .home-integrated-header .header-icon-control .pi-sun,
.app-dark .sfh-main-shell--home .home-integrated-header .header-icon-control .pi-moon {
    color: #dcc88f !important;
}

.app-dark .sfh-main-shell--home .home-integrated-header .header-icon-control:hover,
.app-dark .sfh-main-shell--home .home-integrated-header .header-language-control:hover {
    color: #d9f5f5 !important;
    border-color: rgba(139, 219, 222, 0.38) !important;
    background: rgba(22, 86, 90, 0.9) !important;
}

.app-dark .sfh-main-shell--home .home-integrated-header .spotlight-search .p-inputtext {
    color: #f3f8f8 !important;
    border-color: rgba(139, 219, 222, 0.18) !important;
    background: rgba(9, 31, 33, 0.82) !important;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255,255,255,.05) !important;
}

.app-dark .sfh-main-shell--home .home-integrated-header .spotlight-search .p-inputtext::placeholder {
    color: #98aaaa !important;
}

.app-dark .sfh-main-shell--home .home-integrated-header .spotlight-search .p-inputicon,
.app-dark .sfh-main-shell--home .home-integrated-header .spotlight-search .pi-search {
    color: #8bd7da !important;
}

.app-dark .sfh-main-shell--home .home-integrated-header .spotlight-shortcut {
    color: #d5e1e1 !important;
    border-color: rgba(139, 219, 222, 0.16) !important;
    background: rgba(255, 255, 255, 0.08) !important;
}

.app-dark .sfh-main-shell--home .home-integrated-header .header-profile-control img {
    box-shadow: 0 0 0 3px rgba(139, 219, 222, 0.42), 0 10px 24px rgba(0, 0, 0, 0.3) !important;
}
</style>
